// Hook returning the full department list, sourced from RTDB.
//
// On first ever mount in a workspace, the four built-in departments
// (audit / implementation / strategic / brand) are seeded into RTDB at
// their canonical IDs via seedDefaultDepartmentsIfNeeded — after that,
// every department is just a row in projectDepartments and any of them
// can be deleted. The DEFAULT_DEPARTMENTS constants are now only the
// SEED SOURCE, not a runtime merge fallback (except briefly during
// initial load, so the UI doesn't flash empty before the subscription
// or seed completes).

import { useEffect, useMemo, useState } from 'react';

import { DEFAULT_DEPARTMENTS, DepartmentDef } from './constants';
import { seedDefaultDepartmentsIfNeeded, subscribeProjectDepartments } from './firebase';
import { getCurrentUser } from '../firebase';
import { ProjectDepartmentDef } from './types';

interface UseProjectDepartmentsResult {
  /** Final list to render: post-seed, this is exactly what's in RTDB. */
  departments: DepartmentDef[];
  /** Lookup helper across the merged list — undefined when the id no
   *  longer resolves (e.g. department was just deleted). */
  getDept: (id: string | undefined) => DepartmentDef | undefined;
}

// Module-level guard so seeding only runs once per page load even if
// multiple components call the hook simultaneously. Idempotency is also
// enforced server-side via the projectDepartmentsMeta/seeded flag, but
// this avoids the network round-trip in the common case.
let seedingPromise: Promise<void> | null = null;

export function useProjectDepartments(): UseProjectDepartmentsResult {
  const [items, setItems] = useState<ProjectDepartmentDef[]>([]);
  // Tracks whether we've received at least one RTDB snapshot yet — used
  // to decide between "still loading, show defaults" and "loaded but
  // empty, show empty" (the latter only happens if a user deleted
  // every department, which is fine).
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const unsub = subscribeProjectDepartments((next) => {
      setItems(next);
      setLoaded(true);
    });
    return unsub;
  }, []);

  // Kick off the one-shot seed. Reads getCurrentUser once for the
  // createdBy stamp; harmless if it resolves to no user (the write will
  // just fail on the RTDB rule, which is the correct behavior for
  // unauthenticated callers).
  useEffect(() => {
    if (seedingPromise) return;
    seedingPromise = (async () => {
      try {
        const user: any = await getCurrentUser();
        if (!user) return;
        await seedDefaultDepartmentsIfNeeded(user.uid || '');
      } catch {
        // Seeding failure is non-fatal — the user can still see the
        // pre-seed DEFAULT_DEPARTMENTS fallback below, and a future
        // mount will retry.
        seedingPromise = null;
      }
    })();
  }, []);

  const departments = useMemo<DepartmentDef[]>(() => {
    // Pre-load (haven't heard from RTDB yet): show DEFAULT_DEPARTMENTS
    // so the picker / Grid lanes never render empty.
    if (!loaded) return DEFAULT_DEPARTMENTS;
    // Post-seed: RTDB has the canonical list. Even if a user has
    // deleted every department, we honor that and return [].
    return items.map((d) => ({
      id: d.id,
      label: d.label,
      swatch: d.swatch,
      color: d.color,
    }));
  }, [items, loaded]);

  const getDept = useMemo(() => {
    const map = new Map<string, DepartmentDef>();
    for (const d of departments) map.set(d.id, d);
    return (id: string | undefined) => (id ? map.get(id) : undefined);
  }, [departments]);

  return { departments, getDept };
}
